import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { JobType } from '@app/features/job-types/job-type.model';
import { SaveButton } from '@app/shared/buttons/save-button/save-button';
import moment from 'moment';
import { SalaryScaleForm } from '../salary-scale-form/salary-scale-form';
import { SalaryScale } from '../salary-scale.model';
import { SalaryScalesService } from '../salary-scales-service';

export interface SalaryScaleDialogData {
  jobType: JobType;
  editSalaryScale?: SalaryScale;
}

@Component({
  selector: 'app-salary-scale-dialog',
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogContent,
    MatDialogActions,
    SaveButton,
    SalaryScaleForm,
  ],
  templateUrl: './salary-scale-dialog.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./salary-scale-dialog.scss'],
})
export class SalaryScaleDialog implements OnInit {
  readonly cdr = inject(ChangeDetectorRef);
  readonly dialogRef = inject(MatDialogRef<SalaryScaleDialog>);
  readonly data = inject<SalaryScaleDialogData>(MAT_DIALOG_DATA);
  readonly salaryScalesService = inject(SalaryScalesService);

  salaryScale!: SalaryScale;
  @ViewChild(SalaryScaleForm) salaryScaleForm?: SalaryScaleForm;
  @ViewChild(SaveButton) saveButton!: SaveButton;

  ngOnInit(): void {
    if (this.data.editSalaryScale) {
      this.salaryScale = this.data.editSalaryScale;
    } else {
      this.salaryScale = {
        jobTypeId: this.data.jobType.id,
        startDate: '',
        entries: this.data.jobType.qualificationLevels.map((qualificationLevel) => ({
          qualificationLevel,
          grossHourlySalary: 0,
        })),
      };
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    if (!this.salaryScaleForm || !this.salaryScaleForm.formGroup.valid) {
      return;
    }
    this.saveButton.startSaving();
    if (this.data.editSalaryScale) {
      this.update();
    } else {
      this.create();
    }
  }

  create() {
    const formValue = this.salaryScaleForm!.formGroup.getRawValue();
    this.salaryScalesService
      .create({
        startDate: moment(formValue.startDate).format('YYYY-MM-DD'),
        entries: formValue.entries,
        jobTypeId: this.data.jobType.id,
      })
      .subscribe({
        next: (response) => {
          this.saveButton.stopSaving(true);
          this.dialogRef.close(response);
        },
        error: (err) => {
          this.saveButton.stopSaving(false);
          console.error('Error creating salary scale', err);
        },
      });
  }

  update() {
    const formValue = this.salaryScaleForm!.formGroup.getRawValue();
    this.salaryScalesService
      .update({
        startDate: moment(formValue.startDate).format('YYYY-MM-DD'),
        entries: formValue.entries,
        jobTypeId: this.data.jobType.id,
        id: this.data.editSalaryScale!.id,
      })
      .subscribe({
        next: (response) => {
          this.saveButton.stopSaving(true);
          this.dialogRef.close(response);
        },
        error: (err) => {
          this.saveButton.stopSaving(false);
          console.error('Error updating salary scale', err);
        },
      });
  }
}
